import React, { useState } from 'react';
import Modal from '@/components/common/Modal';
import Badge from '@/components/common/Badge';
import PatenteDisplay from '@/components/common/PatenteDisplay';
import HistorialMantenimientos from '@/components/vehiculos/HistorialMantenimientos';
import { formatPhone, formatRut } from '@/utils/formatters';
import { normalizeDataObject } from '@/utils/normalizationUtils';
import moment from 'moment';
import {
  FaUser,
  FaPhone,
  FaEnvelope,
  FaIdCard,
  FaMapMarkerAlt,
  FaCar,
  FaCalendarAlt,
} from 'react-icons/fa';

/**
 * Modal para mostrar los detalles completos de un cliente
 * Incluye información personal y vehículos registrados
 */
const ClienteDetalleModal = ({ isOpen, onClose, cliente }) => {
  // Estado para controlar el modal de historial de mantenimientos
  const [historialModalOpen, setHistorialModalOpen] = useState(false);
  const [selectedPatente, setSelectedPatente] = useState('');
  
  // Normalizar datos del cliente
  const normalizedCliente = normalizeDataObject(cliente, 'cliente');
  
  // Obtener datos del cliente
  const nombre = normalizedCliente.nombre || normalizedCliente.Nombre || '';
  const apellido = normalizedCliente.apellido || normalizedCliente.Apellido || '';
  const nombreCompleto = `${nombre}${apellido ? ' ' + apellido : ''}`;
  const rut = normalizedCliente.rut || normalizedCliente.RUT || '';
  const rutFormateado = rut ? formatRut(rut) : '';
  const email = normalizedCliente.email || normalizedCliente.Correo || '';
  const telefono = normalizedCliente.telefono || normalizedCliente.Telefono || '';
  const direccion = normalizedCliente.direccion || normalizedCliente.Direccion || '';
  const clienteId = normalizedCliente.id || normalizedCliente.Id;
  const isActivo = normalizedCliente.activo !== false;
  const cantidadVehiculos = normalizedCliente.vehiculos_count || 0;
  const ultimoServicio = moment(normalizedCliente.ultimo_servicio).format('DD/MM/YYYY') || '-';

  // Usar vehículos que ya vienen en los datos del cliente
  const vehiculos = normalizedCliente.vehiculos || [];
  const loadingVehiculos = false; // Ya no necesitamos loading porque los datos ya están

  // Función para abrir el historial de mantenimientos de un vehículo
  const handleVehiculoClick = (patente) => {
    setSelectedPatente(patente);
    setHistorialModalOpen(true);
  };

  // Función para cerrar el modal de historial
  const handleHistorialClose = () => {
    setHistorialModalOpen(false);
    setSelectedPatente('');
  };

  if (!cliente) return null;

  const modalTitle = (
    <div className="flex items-center gap-4">
      <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center text-white font-semibold text-lg flex-shrink-0">
        {nombre.charAt(0).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <span>Detalles del Cliente</span>
        <p className="text-gray-400 text-sm font-normal mt-1">{nombreCompleto}</p>
      </div>
    </div>
  );

  const modalFooter = (
    <div className="flex justify-end">
      <button
        onClick={onClose}
        className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg text-sm sm:text-base"
      >
        Cerrar
      </button>
    </div>
  );

  return (
    <>
      <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={modalTitle}
      size="2xl"
      footer={modalFooter}
    >
          <div className="space-y-6">
            
            {/* Información Personal */}
            <div className="border border-gray-600 rounded-lg p-4 relative">
              <div className="flex items-center mb-4">
                <FaUser className="w-5 h-5 text-blue-400 mr-2" />
                <h3 className="text-lg font-semibold text-white">Información Personal</h3>
              </div>
              {/* Línea divisoria */}
              <div className="border-t border-gray-600 mb-4"></div>
              
              {/* Badge de Estado en esquina superior derecha */}
              <div className="absolute top-4 right-4">
                <Badge variant={isActivo ? "success" : "secondary"} size="sm">
                  {isActivo ? "Activo" : "Inactivo"}
                </Badge>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Nombre Completo */}
                <div className="md:col-span-2">
                  <div className="flex items-center">
                    <FaUser className="w-4 h-4 text-gray-500 mr-2" />
                    <p className="text-white font-medium text-lg">{nombreCompleto}</p>
                  </div>
                </div>
                
                {/* RUT y Teléfono en la misma fila */}
                <div>
                  <div className="flex items-center">
                    <FaIdCard className="w-4 h-4 text-gray-500 mr-2" />
                    <p className="text-white">{rutFormateado || 'RUT no especificado'}</p>
                  </div>
                </div>
                
                <div>
                  <div className="flex items-center">
                    <FaPhone className="w-4 h-4 text-gray-500 mr-2" />
                    <p className="text-white">{telefono ? formatPhone(telefono) : 'Teléfono no especificado'}</p>
                  </div>
                </div>
                
                {/* Email */}
                <div className="md:col-span-2">
                  <div className="flex items-center">
                    <FaEnvelope className="w-4 h-4 text-gray-500 mr-2" />
                    <p className="text-white truncate">{email || 'Email no especificado'}</p>
                  </div>
                </div>
                
                {/* Dirección */}
                {direccion && (
                  <div className="md:col-span-2">
                    <div className="flex items-center">
                      <FaMapMarkerAlt className="w-4 h-4 text-gray-500 mr-2" />
                      <p className="text-white">{direccion}</p>
                    </div>
                  </div>
                )}
                
                {/* Último Servicio */}
                <div className="md:col-span-2">
                  <div className="flex items-center">
                    <FaCalendarAlt className="w-4 h-4 text-gray-500 mr-2" />
                    <span className="text-gray-400 mr-2">Último Servicio:</span>
                    <p className="text-white">{ultimoServicio}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Vehículos Registrados */}
            <div className="border border-gray-600 rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <FaCar className="w-5 h-5 text-blue-400 mr-2" />
                  <h3 className="text-lg font-semibold text-white">
                    Vehículos Registrados ({cantidadVehiculos})
                  </h3>
                </div>
              </div>
              {/* Línea divisoria */}
              <div className="border-t border-gray-600 mb-4"></div>
              
              {loadingVehiculos ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                  <span className="ml-3 text-gray-400">Cargando vehículos...</span>
                </div>
              ) : vehiculos.length > 0 ? (
                <div className="space-y-3">
                  {vehiculos.map((vehiculo, index) => (
                    <div
                      key={vehiculo.patente || index}
                      onClick={() => handleVehiculoClick(vehiculo.patente)}
                      className="border border-gray-600 rounded-lg p-4 hover:border-gray-500 hover:bg-gray-700 transition-colors relative cursor-pointer"
                      title="Click para ver historial de mantenimientos"
                    >
                      {/* Fila 1: Marca/Modelo con Badge en esquina superior derecha */}
                      <div className="flex justify-between items-start mb-3">
                        <h4 className="font-semibold text-white text-lg pr-16">
                          {vehiculo.marca_local || vehiculo.marca} {vehiculo.modelo_local || vehiculo.modelo}
                          {vehiculo.año && ` (${vehiculo.año})`}
                        </h4>
                        <div className="absolute top-4 right-4">
                          <Badge
                            variant={vehiculo.activo !== false ? "success" : "secondary"}
                            size="sm"
                          >
                            {vehiculo.activo !== false ? "Activo" : "Inactivo"}
                          </Badge>
                        </div>
                      </div>
                      
                      {/* Fila 2: Patente, Color, Kilometraje */}
                      <div className="flex items-center text-sm text-gray-300 space-x-3">
                        <PatenteDisplay patente={vehiculo.patente} size="xxs" />
                        {vehiculo.color && (
                          <>
                            <span>•</span>
                            <span>{vehiculo.color}</span>
                          </>
                        )}
                        {vehiculo.kilometraje && (
                          <>
                            <span>•</span>
                            <span>{vehiculo.kilometraje.toLocaleString()} km</span>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <FaCar className="w-12 h-12 text-gray-500 mx-auto mb-3" />
                  <p className="text-gray-400">No hay vehículos registrados</p>
                </div>
              )}
            </div>
          </div>
      </Modal>

      {/* Modal de Historial de Mantenimientos */}
      <HistorialMantenimientos
        isOpen={historialModalOpen}
        onClose={handleHistorialClose}
        patente={selectedPatente}
      />
    </>
  );
};

export default ClienteDetalleModal;